import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import styles from '@/styles/components/sort-filter.module.scss';
import { Dropdown } from 'antd';
import SelectDrawer from '@/components/common/select-drawer';
import classNames from 'classnames';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { changeIsOpen, changeProductSelected, changeTopicSelected, changeSortBy, changeDataType, changeStartTimestamp, changeEndTimestamp } from '@/store/slices/sortFilterSlice';
import { getTopicAndProductTags } from '@/utils/dataFormat';
import { AppState } from '@/store';
import dayjs from 'dayjs';
import Image from 'next/image';

type Props = {
  tags: string,
  className?: string;
  category?: string;
  onSearch: () => void;
  onReset: () => void;
  onSort: (sort:string) => void;
  language: any;
}

const SortFilter: FC<Props> = memo((props) => {
  const { className, tags, category, onSearch, onReset, onSort, language } = props;
  const dispatch = useDispatch<any>();
  const [open, setOpen] = useState(false);

  const {
    cateTags,
    productTags,
    sortBy,
    sortFilter
  } = useSelector((state: AppState) => ({
    cateTags: state.categoryPage.cateTags,
    productTags: state.categoryPage.productTags,
    sortBy: state.sortFilter.sortBy,
    sortFilter: state.sortFilter
  }), shallowEqual);

  const handleTags = useCallback( () => {
    const [filterCateTags, filterProductTags] = getTopicAndProductTags(tags, cateTags, productTags);
    dispatch(changeTopicSelected(filterCateTags));
    dispatch(changeProductSelected(filterProductTags));
    return [filterCateTags, filterProductTags];
  }, [cateTags, dispatch, productTags, tags])

  useEffect(() => {
    handleTags();
  }, [handleTags]);

  const handleSearch = () => {

    onSearch && onSearch();
  }

  const handleReset = () => {
    const timestamp = Math.floor(dayjs().valueOf() / 1000);
    dispatch(changeDataType(1));
    dispatch(changeStartTimestamp(0));
    dispatch(changeEndTimestamp(timestamp));
    onReset && onReset()
  }

  const handleSort = async(sort:string) => {

    if (sortBy === sort) {
      return
    }
    await dispatch(changeSortBy(sort))
    onSort(sort)
    setOpen(false);
  }

  const handleOpenChange = (flag: boolean) => {
    setOpen(flag);
  };
  const sortItems = [
    {
      labelEn: 'Most Recent',
      labelZh: '按最新文章排序',
      key: 'publishDate'
    },
    {
      labelEn: 'Least Recent',
      labelZh: '按最舊文章排序',
      key: 'publishDateASC'
    }
  ]

  const handleFilterBtnStyle = () => {
    const { dataType, prodTagEntryIds, topicTagEntryIds } = sortFilter
    return dataType !== 1 || prodTagEntryIds.length !== 0 || topicTagEntryIds.length !== 0;

  }

  return (
    <div className={classNames(styles.Sort_Filter, className, language === 'en' ? styles.Sort_Filter_EN : styles.Sort_Filter_ZH)}>
      <div>
        <button onClick={() => dispatch(changeIsOpen(true))} className={classNames(styles.Filter_Button, language === 'en' ? styles.Filter_Button_En : '')}>
          <Image src="/blog/icon/Filter.svg" className={styles.Filter_Icon} alt={''} width={12} height={13} />
          <Image src="/blog/icon/FilterHover.svg" className={styles.Filter_Icon_Hover} alt={''} width={12} height={13} />
          <Image src="/blog/icon/FilterWhite.svg" className={styles.Filter_Icon_White} alt={''} width={12} height={13}/>
          <span className={styles.Filter_Content}>{language === 'zh' ? '篩選' : 'Filters'}</span>
        </button>

        <Dropdown
          trigger={['click']}
          placement="bottomLeft" arrow
          className={classNames(styles.Sort_Button)}
          onOpenChange={handleOpenChange}
          open={open}
          dropdownRender={(menu) => (
            <ul className={classNames(styles.Sort_Option, language === 'zh' ? styles.Sort_Option_ZH : styles.Sort_Option_EN)}>
              {
                sortItems.map((item: any) =>  <li key={item.key} onClick={() => handleSort(item.key)}>
                <span>{ language === 'zh' ? item.labelZh : item.labelEn}</span>
                {sortBy === item.key ? <Image src="/blog/icon/Tick.svg" className={styles.Tick} alt={''} width={11} height={9}/> : ''}
                {sortBy === item.key ? <Image src="/blog/icon/TickWhite.svg" className={styles.Tick_White} alt={''} width={11} height={9}/> : ''}
              </li>)
              }
            </ul>
          )}
        >
          <button className={classNames(styles.Sort_Btn, language === 'en' ? styles.Sort_Btn_En : '')}>
            <Image src="/blog/icon/Sort.svg" className={styles.Sort_Icon} alt={''} width={13} height={13}/>
            <Image src="/blog/icon/SortWhite.svg" className={styles.Sort_Icon_White} alt={''} width={13} height={13}/>
            <span className={styles.Sort_Content}>{language === 'zh' ? '排序方式' : 'Sort'}</span>
          </button>
        </Dropdown>
      </div>

      <button onClick={handleReset} className={styles.Reset_Button}>
        <Image src="/blog/icon/Redo.svg" alt={''} width={12} height={11}/>
        <span  className={styles.Reset_Content}>{language === 'zh' ? '重設' : 'Reset'}</span>
      </button>

      <SelectDrawer
        tags={tags}
        category={category}
        onSearch={handleSearch}
        language={language}
      />
    </div>
  )
});

export default SortFilter;
